import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

import { DxcBreakpoint, dxcBreakpointQuery } from '../media/dxc-media';

/**
 * Le dice a una franja que se desplaza en horizontal cuánto le queda por
 * enseñar, para que pueda vivir sin barra de desplazamiento a la vista.
 *
 * No pinta nada: solo mantiene tres clases en el anfitrión, y el CSS de la
 * primitiva hace el resto.
 *
 *   `is-scrollable`  el contenido no cabe.
 *   `is-at-start`    está al principio: no hay nada escondido a la izquierda.
 *   `is-at-end`      está al final: no hay nada escondido a la derecha.
 *   `is-hinting`     una sola vez, la primera que resulta desplazable: es la
 *                    señal para el gesto de entrada que avisa de que hay más.
 *
 * Va en una directiva y no en CSS porque «¿cabe?» es una medida, y CSS no
 * sabe leer `scrollWidth`. Todo corre fuera de la zona: ninguna de estas
 * clases es un dato de Angular.
 *
 * Con `dxcScrollHintBelow` solo mide por debajo de un peldaño de la escala
 * (`lg`, `md`, `sm`). Es la forma de ponerla en una primitiva cuyo aviso solo se
 * pinta en el tramo responsive —el carril de pestañas, la caja de la tabla—:
 * en escritorio no hay observadores ni clases, así que el árbol queda idéntico
 * al de siempre (R-DESK).
 *
 * Con `dxcScrollHintReveal` (un selector) lleva además a la vista el hijo que
 * casa —la pestaña elegida— al empezar a medir y cada vez que cambia de dueño su
 * `aria-current`/`aria-selected`. A 390 la pestaña Correos de Ajustes y la
 * sección activa del detalle de la carga quedaban fuera del carril, y nada
 * decía cuál estaba elegida. Solo se mueve el carril, nunca la página, y solo
 * cuando cambia la elegida: un gesto del usuario sobre el carril no se pisa.
 *
 * @example
 * <dl class="dxc-kpis" dxcScrollHint>…</dl>
 * <nav class="dxc-tabs" dxcScrollHint dxcScrollHintBelow="lg" dxcScrollHintReveal='[aria-current="page"]'>…</nav>
 * <div class="dxc-table-wrap" dxcScrollHint dxcScrollHintBelow="lg">…</div>
 */
/** Lo que funde cada canto la mezcla `dxc-scroll-fade` de la hoja. */
const FADE = 32;

@Directive({
    standalone: true,
    selector: '[dxcScrollHint]',
})
export class DxcScrollHintDirective implements AfterViewInit, OnDestroy {

    /** Mide solo por debajo de este peldaño. Vacío: en todos los anchos. */
    @Input() dxcScrollHintBelow: DxcBreakpoint | '' = '';

    /** Selector del hijo que tiene que estar a la vista (la pestaña elegida). */
    @Input() dxcScrollHintReveal = '';

    private resize?: ResizeObserver;
    private mutation?: MutationObserver;
    private gate?: MediaQueryList;
    private hinted = false;
    private revealed: Element | null = null;
    private readonly onScroll = () => this.measure();
    private readonly onGate = () => this.gate?.matches ? this.start() : this.stop();

    constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => {
            if (!this.dxcScrollHintBelow) return this.start();
            this.gate = window.matchMedia(dxcBreakpointQuery(this.dxcScrollHintBelow));
            this.gate.addEventListener('change', this.onGate);
            this.onGate();
        });
    }

    ngOnDestroy(): void {
        this.gate?.removeEventListener('change', this.onGate);
        this.stop();
    }

    private start(): void {
        if (this.resize || this.mutation) return;
        const el = this.host.nativeElement;
        el.addEventListener('scroll', this.onScroll, { passive: true });
        if (typeof ResizeObserver === 'function') {
            this.resize = new ResizeObserver(() => { this.measure(); this.reveal(); });
            this.resize.observe(el);
        }
        // El ancho del contenido cambia sin que cambie el del anfitrión: al
        // llegar las cifras, o al cambiar de periodo.
        if (typeof MutationObserver === 'function') {
            this.mutation = new MutationObserver(() => { this.measure(); this.reveal(); });
            // Las clases que pone `measure()` van en el anfitrión: el filtro deja
            // fuera `class`, o cada medida se volvería a avisar a sí misma.
            this.mutation.observe(el, this.dxcScrollHintReveal
                ? { childList: true, subtree: true, characterData: true, attributeFilter: ['aria-current', 'aria-selected'] }
                : { childList: true, subtree: true, characterData: true });
        }
        this.measure();
        this.reveal();
    }

    /** Fuera del tramo: sin escuchas y sin clases, como si no estuviera. */
    private stop(): void {
        const el = this.host.nativeElement;
        el.removeEventListener('scroll', this.onScroll);
        this.resize?.disconnect();
        this.mutation?.disconnect();
        this.resize = this.mutation = undefined;
        this.revealed = null;
        el.classList.remove('is-scrollable', 'is-at-start', 'is-at-end', 'is-hinting');
    }

    /**
     * Sin `requestAnimationFrame`: los tres avisos que llaman aquí —scroll,
     * cambio de tamaño y cambio de contenido— ya llegan como mucho una vez por
     * fotograma, y con la pestaña oculta `requestAnimationFrame` no llega
     * nunca, así que las clases se quedarían con la medida anterior.
     */
    private measure(): void {
        const el = this.host.nativeElement;
        const overflow = el.scrollWidth - el.clientWidth;
        const scrollable = overflow > 1;

        el.classList.toggle('is-scrollable', scrollable);
        el.classList.toggle('is-at-start', el.scrollLeft <= 1);
        el.classList.toggle('is-at-end', el.scrollLeft >= overflow - 1);

        if (scrollable && !this.hinted) {
            this.hinted = true;
            el.classList.add('is-hinting');
        }
    }

    /**
     * A mano y no con `scrollIntoView`: aquel desplaza también la página si el
     * carril está bajo el pliegue, y lo único que tiene que moverse es el carril.
     */
    private reveal(): void {
        if (!this.dxcScrollHintReveal) return;
        const el = this.host.nativeElement;
        const target = el.querySelector(this.dxcScrollHintReveal);
        if (!target || target === this.revealed || el.scrollWidth <= el.clientWidth + 1) return;
        this.revealed = target;
        const rail = el.getBoundingClientRect();
        const box = target.getBoundingClientRect();
        const start = box.left - rail.left + el.scrollLeft;
        const end = start + box.width;
        // Los cantos se funden 32 px (`dxc-scroll-fade`): una pestaña que llega
        // justo al borde queda a medias bajo el desvanecido. Si no está entera
        // dentro de la franja nítida, se centra; los extremos los recorta el
        // propio navegador al valor válido.
        if (start < el.scrollLeft + FADE || end > el.scrollLeft + el.clientWidth - FADE) {
            el.scrollLeft = Math.round(start - (el.clientWidth - box.width) / 2);
        }
    }
}
